import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  DocumentData
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from './firebase';
import { LostItem, FoundItem, ClaimRequest, SearchFilters } from '../types';

// Collections
const LOST_ITEMS = 'lostItems';
const FOUND_ITEMS = 'foundItems';
const CLAIM_REQUESTS = 'claimRequests';

// Helper to convert Firestore data to typed objects
const convertTimestamps = <T extends DocumentData>(data: DocumentData): T => {
  const converted = { ...data };
  Object.keys(converted).forEach(key => {
    if (converted[key] instanceof Timestamp) {
      converted[key] = converted[key].toDate();
    }
  });
  return converted as T;
};

// Lost Items CRUD
export const createLostItem = async (item: Omit<LostItem, 'id' | 'createdAt' | 'updatedAt'>) => {
  const docRef = await addDoc(collection(db, LOST_ITEMS), {
    ...item,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
};

export const updateLostItem = async (id: string, updates: Partial<LostItem>) => {
  await updateDoc(doc(db, LOST_ITEMS, id), {
    ...updates,
    updatedAt: Timestamp.now()
  });
};

export const deleteLostItem = async (id: string) => {
  await deleteDoc(doc(db, LOST_ITEMS, id));
};

export const getLostItem = async (id: string): Promise<LostItem | null> => {
  const docSnap = await getDoc(doc(db, LOST_ITEMS, id));
  if (docSnap.exists()) {
    const data = convertTimestamps(docSnap.data() as LostItem);
    return { ...data, id: docSnap.id } as LostItem;
  }
  return null;
};

export const getLostItems = async (filters?: SearchFilters): Promise<LostItem[]> => {
  let q = query(collection(db, LOST_ITEMS), orderBy('createdAt', 'desc'));
  
  if (filters?.category) {
    q = query(q, where('category', '==', filters.category));
  }
  if (filters?.status) {
    q = query(q, where('status', '==', filters.status));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => 
    convertTimestamps({ id: doc.id, ...doc.data() })
  );
};

// Found Items CRUD
export const createFoundItem = async (item: Omit<FoundItem, 'id' | 'createdAt' | 'updatedAt'>) => {
  const docRef = await addDoc(collection(db, FOUND_ITEMS), {
    ...item,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
};

export const updateFoundItem = async (id: string, updates: Partial<FoundItem>) => {
  await updateDoc(doc(db, FOUND_ITEMS, id), {
    ...updates,
    updatedAt: Timestamp.now()
  });
};

export const deleteFoundItem = async (id: string) => {
  await deleteDoc(doc(db, FOUND_ITEMS, id));
};

export const getFoundItem = async (id: string): Promise<FoundItem | null> => {
  const docSnap = await getDoc(doc(db, FOUND_ITEMS, id));

  if (docSnap.exists()) {
    // Cast the output of the conversion function to FoundItem
    return convertTimestamps({ 
      ...docSnap.data(), 
      id: docSnap.id 
    }) as FoundItem; 
  }

  return null;
};

export const getFoundItems = async (filters?: SearchFilters): Promise<FoundItem[]> => {
  let q = query(collection(db, FOUND_ITEMS), orderBy('createdAt', 'desc'));
  
  if (filters?.category) {
    q = query(q, where('category', '==', filters.category));
  }
  if (filters?.status) {
    q = query(q, where('status', '==', filters.status));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => 
    convertTimestamps({ id: doc.id, ...doc.data() })
  );
};

// User-specific queries
export const getUserLostItems = async (userId: string): Promise<LostItem[]> => {
  const q = query(
    collection(db, LOST_ITEMS),
    where('reportedBy', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc =>
    convertTimestamps({ id: doc.id, ...doc.data() })
  );
};

export const getUserFoundItems = async (userId: string): Promise<FoundItem[]> => {
  const q = query(
    collection(db, FOUND_ITEMS),
    where('foundBy', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc =>
    convertTimestamps({ id: doc.id, ...doc.data() })
  );
};

// Image Upload
export const uploadItemImage = async (file: File, itemId: string): Promise<string> => {
  const storageRef = ref(storage, `items/${itemId}/${file.name}`);
  await uploadBytes(storageRef, file);
  const downloadURL = await getDownloadURL(storageRef);
  return downloadURL;
};

export const deleteItemImage = async (imageUrl: string) => {
  const imageRef = ref(storage, imageUrl);
  await deleteObject(imageRef);
};

// Claim Requests
export const createClaimRequest = async (claim: Omit<ClaimRequest, 'id' | 'createdAt' | 'updatedAt'>) => {
  const docRef = await addDoc(collection(db, CLAIM_REQUESTS), {
    ...claim,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  });
  return docRef.id;
};

export const updateClaimRequest = async (id: string, updates: Partial<ClaimRequest>) => {
  await updateDoc(doc(db, CLAIM_REQUESTS, id), {
    ...updates,
    updatedAt: Timestamp.now()
  });
};

export const getClaimRequests = async (userId?: string): Promise<ClaimRequest[]> => {
  let q = query(collection(db, CLAIM_REQUESTS), orderBy('createdAt', 'desc'));
  
  if (userId) {
    q = query(q, where('claimantId', '==', userId));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => 
    convertTimestamps({ id: doc.id, ...doc.data() })
  );
};

export const getPendingClaimRequests = async (): Promise<ClaimRequest[]> => {
  const q = query(
    collection(db, CLAIM_REQUESTS),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => 
    convertTimestamps({ id: doc.id, ...doc.data() })
  );
};

// Search functionality
export const searchItems = async (
  searchTerm: string,
  itemType: 'lost' | 'found' | 'both' = 'both'
): Promise<{ lostItems: LostItem[]; foundItems: FoundItem[] }> => {
  const results = { lostItems: [] as LostItem[], foundItems: [] as FoundItem[] };
  
  if (itemType === 'lost' || itemType === 'both') {
    const lostQuery = query(
      collection(db, LOST_ITEMS),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const lostSnapshot = await getDocs(lostQuery);
    results.lostItems = lostSnapshot.docs
      .map(doc => convertTimestamps<LostItem>({ id: doc.id, ...doc.data() }))
      .filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.location?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
      );
  }

  if (itemType === 'found' || itemType === 'both') {
    const foundQuery = query(
      collection(db, FOUND_ITEMS),
      where('status', '==', 'active'),
      orderBy('createdAt', 'desc'),
      limit(50)
    );
    const foundSnapshot = await getDocs(foundQuery);
    results.foundItems = foundSnapshot.docs
      .map(doc => convertTimestamps<FoundItem>({ id: doc.id, ...doc.data() }))
      .filter(item => 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.location?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false)
      );
  }

  return results;
};
